import { useCallback, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { BASE_URL, EDIT_MODES } from '../constants'

import {
  fetchSuccess,
  fetchStart,
  fetchFail,
  hoverStart,
  hoverEnd,
  showBuffer,
  hideBuffer,
  selectBuffer,
  deselectBuffer,
  openEditor,
  closeEditor,
  editBuffer,
  addBuffer,
  removeBuffer,
  markBuffersClean,
  setEditDistance,
  setEditLayer,
  setEditUnit,
} from './actions'

import {
  getBufferByLayerId,
  getLayerIds,
  getBuffers,
  getSelectedByLayerId,
  getVisibleByLayerId,
  getSelectedBuffer,
  getEditor,
  getEditorIsOpen,
  getIsClean,
  getIsLoading,
  getHoveredBuffer,
  getEditorBuffer,
} from './selectors'

import useFetch from '../hooks/useFetch'
import { useToken } from '../auth/hooks'
import { useSelectedIncident } from '../incidents/hooks'

export const useBuffersFetch = () => {
  const dispatch = useDispatch()

  const { incidentTypeCode, crimeCategory, incidentNumber } =
    useSelectedIncident() || {}

  useFetch(
    `${BASE_URL}api/AIBuffers/${crimeCategory}/${incidentTypeCode}`,
    {
      onStart: () => dispatch(fetchStart()),
      onSuccess: (buffers) => dispatch(fetchSuccess(buffers)),
      onError: (error) => dispatch(fetchFail(error)),
    },
    [incidentTypeCode, crimeCategory, incidentNumber],
    (crimeCategory && incidentTypeCode) || false // undefined would be like passing no parameter
  )
}

export const useCurrentBuffersLayerIds = () => {
  const layerIds = useSelector(getLayerIds)
  return layerIds
}

export const useBufferList = () => {
  const dispatch = useDispatch()
  const layerIds = useSelector(getLayerIds)
  const isClean = useSelector(getIsClean)
  const isLoading = useSelector(getIsLoading)
  const token = useToken()
  const { incidentTypeCode } = useSelectedIncident() || {}

  const addBuffer = useCallback(() => {
    dispatch(openEditor())
  }, [dispatch])

  const saveBuffers = useCallback(() => {
    dispatch((dispatch, getState) => {
      const buffers = getBuffers(getState())
      fetch(`${BASE_URL}api/AIBuffers/save/${incidentTypeCode}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(buffers),
      })
      dispatch(markBuffersClean())
    })
  }, [dispatch, incidentTypeCode, token])

  return [layerIds, isLoading, isClean, { addBuffer, saveBuffers }]
}

export const useBuffer = (layerId) => {
  const dispatch = useDispatch()
  const buffer = useSelector(getBufferByLayerId)(layerId)
  buffer.isSelected = useSelector(getSelectedByLayerId)(layerId)
  buffer.isVisible = useSelector(getVisibleByLayerId)(layerId)

  const actions = useMemo(
    () => ({
      hover: () => dispatch(hoverStart(layerId)),
      endHover: () => dispatch(hoverEnd(layerId)),
      show: () => dispatch(showBuffer(layerId)),
      hide: () => dispatch(hideBuffer(layerId)),
      select: () => dispatch(selectBuffer(layerId)),
      deselect: () => dispatch(deselectBuffer(layerId)),
      edit: () => dispatch(openEditor(buffer, EDIT_MODES.EDIT)),
    }),
    [layerId, dispatch, buffer]
  )
  return [buffer, actions]
}

export const useSelectedBuffer = () => {
  const buffer = useSelector(getSelectedBuffer)
  return buffer
}

export const useHoveredBuffer = () => {
  const buffer = useSelector(getHoveredBuffer)
  return buffer
}

export const useBufferEditorIsOpen = () => useSelector(getEditorIsOpen)

export const useBufferMap = () => {
  const incident = useSelectedIncident()
  const buffer = useHoveredBuffer()
  const editorIsOpen = useBufferEditorIsOpen()
  const editorBuffer = useSelector(getEditorBuffer)

  return [editorIsOpen ? editorBuffer : buffer, incident]
}

export const useBufferEditor = () => {
  const dispatch = useDispatch()
  const { mode, initialBuffer, buffer } = useSelector(getEditor)

  const isEdited = useMemo(() => {
    return (
      mode === EDIT_MODES.NEW ||
      initialBuffer.layerId !== buffer.layerId ||
      initialBuffer.distance !== buffer.distance ||
      initialBuffer.unit !== buffer.unit
    )
  }, [mode, initialBuffer, buffer])

  const actions = useMemo(
    () => ({
      save: () => {
        if (!isEdited) return // don't save if nothing has happened
        if (mode === EDIT_MODES.EDIT) {
          dispatch(editBuffer(buffer))
        } else {
          dispatch(addBuffer(buffer))
        }
      },
      close: () => dispatch(closeEditor()),
      remove: () => dispatch(removeBuffer(buffer.layerId)),
      onChange: {
        layerId: (value) => dispatch(setEditLayer(value)),
        distance: (value) => dispatch(setEditDistance(value)),
        unit: (value) => dispatch(setEditUnit(value)),
      },
    }),
    [dispatch, mode, buffer, isEdited]
  )

  return [{ buffer, mode, isEdited }, actions]
}
